import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography, Alert } from "@mui/material";
import { useSnackbar } from "notistack";
import Spinner from "../Spinner.jsx";
import {
  verifyTwoFactorCode,
  requestTwoFactorChallenge,
} from "../../services/twoFactorService.js";
import { Input, Button, Link } from "../../theme/components/index.js";
import { dimensions } from "../../theme/tokens/dimensions.js";
import { typographyTokens } from "../../theme/index.js";

/**
 * Handles entering and verifying the 2FA code (v2 themed)
 */
export default function TwoFactorVerification({
  email,
  challengeData,
  challengeToken,
  onVerificationSuccess,
  onCancel,
}) {
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [resendTimeLeft, setResendTimeLeft] = useState(null);
  const [canResend, setCanResend] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const codeInputRef = useRef(null);

  useEffect(() => {
    if (!challengeData?.expires_at) {
      return;
    }

    const updateTimeLeft = () => {
      const now = new Date();
      const expiresAt = new Date(challengeData.expires_at);
      const diff = expiresAt - now;

      const nextRequestAllowedAtRaw = challengeData?.next_request_allowed_at;
      const nextRequestAllowedAt = nextRequestAllowedAtRaw
        ? new Date(nextRequestAllowedAtRaw)
        : null;
      const resendDiff = nextRequestAllowedAt ? nextRequestAllowedAt - now : 0;

      if (nextRequestAllowedAt) {
        setResendTimeLeft(Math.max(0, Math.ceil(resendDiff / 1000)));
      } else {
        setResendTimeLeft(null);
      }

      if (diff <= 0) {
        setTimeLeft(0);
        setCanResend(true);
      } else {
        setTimeLeft(Math.ceil(diff / 1000));

        if (nextRequestAllowedAt) {
          setCanResend(resendDiff <= 0);
        } else {
          setCanResend(false);
        }
      }
    };

    updateTimeLeft();
    const interval = setInterval(updateTimeLeft, 1000);
    return () => clearInterval(interval);
  }, [challengeData]);

  useEffect(() => {
    if (codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, []);

  const formatTimeLeft = (seconds) => {
    if (seconds <= 0) {
      return "Abgelaufen";
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const formatResendTimeLeft = (seconds) => {
    if (seconds === null) {
      return "";
    }

    if (seconds <= 0) {
      return "Jetzt";
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();

    if (!code.trim()) {
      setError("Bitte geben Sie den Verifikationscode ein.");
      return;
    }

    if (timeLeft !== null && timeLeft <= 0) {
      setError(
        "Der Code ist abgelaufen. Bitte fordern Sie einen neuen Code an."
      );
      return;
    }

    setError(null);

    try {
      const verificationData = await verifyTwoFactorCode(
        challengeData.challenge_id,
        code.trim(),
        challengeToken,
        {
          setIsLoading,
          onSuccess: (data) => {
            enqueueSnackbar("Verifikation erfolgreich!", {
              variant: "success",
            });
            if (onVerificationSuccess) {
              onVerificationSuccess(data);
            }
          },
          onError: (error_response) => {
            if (error_response?.response?.status === 401) {
              const error_message =
                "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an, um fortzufahren.";
              setError(error_message);
              enqueueSnackbar(error_message, { variant: "error" });
              if (onCancel) {
                onCancel();
              }
              return;
            }

            const attempts_remaining =
              error_response?.response?.data?.data?.attempts_remaining;
            const delay_seconds =
              error_response?.response?.data?.data?.delay_seconds;

            let error_message =
              error_response?.response?.data?.message ||
              "Ungültiger Verifikationscode. Bitte überprüfen Sie Ihre Eingabe.";

            if (
              (delay_seconds === undefined || delay_seconds === null) &&
              attempts_remaining !== undefined &&
              attempts_remaining !== null &&
              Number(attempts_remaining) > 0
            ) {
              error_message += ` (noch ${attempts_remaining} Versuche übrig)`;
            }
            setError(error_message);
            enqueueSnackbar(error_message, { variant: "error" });
          },
        }
      );
      return verificationData;
    } catch (error_verify) {
      console.error("Error verifying 2FA code:", error_verify);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setCode("");

    try {
      await requestTwoFactorChallenge(email, "email", challengeToken, {
        setIsLoading,
        onSuccess: (data) => {
          enqueueSnackbar("Neuer Verifikationscode wurde gesendet.", {
            variant: "success",
          });
          if (onBack) {
            onBack(data);
          }
        },
        onError: (error_response) => {
          if (error_response?.response?.status === 401) {
            const error_message =
              "Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an, um fortzufahren.";
            setError(error_message);
            enqueueSnackbar(error_message, { variant: "error" });
            if (onCancel) {
              onCancel();
            }
            return;
          }

          const error_message =
            error_response?.response?.data?.message ||
            "Fehler beim Senden des neuen Codes. Bitte versuchen Sie es erneut.";
          setError(error_message);
          enqueueSnackbar(error_message, { variant: "error" });
        },
      });
    } catch (error_resend) {
      console.error("Error resending 2FA code:", error_resend);
    }
  };

  const handleCodeChange = (event) => {
    const value = event.target.value.replace(/\s/g, "").toUpperCase();
    if (value.length <= 6) {
      setCode(value);
      setError(null);
    }
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: dimensions["xl"],
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          gap: dimensions["md"],
        }}
      >
        <Typography variant="h6">Verifikationscode eingeben</Typography>
        <Typography
          color="text.secondary"
          sx={{ fontSize: typographyTokens.size.sm }}
        >
          Wir haben einen 6-stelligen Verifikationscode an{" "}
          <strong>{email}</strong> gesendet. Geben Sie den Code unten ein.
          Überprüfen Sie auch Ihren Spam-Ordner, falls Sie keine E-Mail erhalten
          haben.
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {timeLeft !== null && (
        <Typography
          variant="body2"
          color={timeLeft <= 60 ? "error.main" : "text.secondary"}
          sx={{ fontWeight: 600, textAlign: "center" }}
        >
          Code läuft ab in: {formatTimeLeft(timeLeft)}
        </Typography>
      )}

      <Box
        component="form"
        onSubmit={handleVerifyCode}
        sx={{ display: "flex", flexDirection: "column", gap: dimensions["xl"] }}
      >
        <Input
          inputRef={codeInputRef}
          label="Verifikationscode"
          value={code}
          onChange={handleCodeChange}
          placeholder="123456"
          required
          autoComplete="one-time-code"
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9A-Z]*",
            maxLength: 6,
            style: {
              textAlign: "center",
              fontSize: "1.5rem",
              letterSpacing: "0.5rem",
              fontFamily: "monospace",
            },
          }}
          helperText="6-stelliger alphanumerischer Code"
          disabled={timeLeft !== null && timeLeft <= 0}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: dimensions["lg"],
          }}
        >
          <Button
            type="submit"
            variant="primary"
            size="large"
            fullWidth
            disabled={
              isLoading || !code.trim() || (timeLeft !== null && timeLeft <= 0)
            }
          >
            Code überprüfen
          </Button>

          <Button
            variant="tertiary"
            fullWidth
            onClick={onCancel}
            disabled={isLoading}
          >
            Abbrechen
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          gap: dimensions["xs"],
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Code nicht erhalten?
        </Typography>
        <Link
          component="button"
          variant="primary"
          underline="hover"
          disabled={!canResend || isLoading}
          onClick={handleResendCode}
          sx={{
            cursor: !canResend || isLoading ? "default" : "pointer",
            color: !canResend || isLoading ? "text.disabled" : undefined,
          }}
        >
          {canResend
            ? "Neuen Code anfordern"
            : resendTimeLeft !== null
              ? `Neuer Code möglich in ${formatResendTimeLeft(resendTimeLeft)}`
              : "Neuer Code derzeit nicht verfügbar"}
        </Link>
      </Box>
    </Box>
  );
}

TwoFactorVerification.propTypes = {
  email: PropTypes.string.isRequired,
  challengeData: PropTypes.object,
  challengeToken: PropTypes.string.isRequired,
  onVerificationSuccess: PropTypes.func,
  onCancel: PropTypes.func,
};
